// src/pages/GeotagPhotos.jsx
//
// Client-side only property-photo geotagging tool. A broker/owner uploads
// property photos, picks ONE location (search box, map pin, current
// location, or manual lat/long), and this stamps that same location and
// timestamp onto every photo at once - both as a visible "GPS Map Camera"
// style overlay drawn onto the image pixels via Canvas, and as real GPS
// coordinates embedded into the JPEG's EXIF metadata (via piexifjs) - so
// the photos are independently verifiable for property-verification use.
//
// Deliberately has NO backend involvement: nothing is uploaded or stored
// anywhere. Every step (drawing the overlay, writing EXIF, zipping the
// download) runs entirely in the browser - images only ever leave the
// user's machine as the final downloaded ZIP.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useJsApiLoader, GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import piexif from "piexifjs";
import JSZip from "jszip";
import Cookies from "js-cookie";
import {
  MapPin,
  Upload,
  X,
  Download,
  Loader2,
  Navigation,
  Search,
  ImageIcon,
  CheckCircle2,
  Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import apiClient from "../common/apiClient";
import { VERIFY_LISTING_PRICING } from "../constants/feature.constants";

// Isolated paywall for this tool only -- does not touch the Plan/Addon system.
const VERIFY_LISTING_API = "/ijscui/probroker-admin/ceknvuhbd/VerifyListing/protected";
const PLAN_PRICING = VERIFY_LISTING_PRICING;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const GOOGLE_MAPS_LIBRARIES = ["places"];

// Ahmedabad - PRObroker's home market - default map center until the user
// picks or searches for a real location.
const DEFAULT_CENTER = { lat: 23.0225, lng: 72.5714 };
const MAP_CONTAINER_STYLE = { width: "100%", height: "260px", borderRadius: "12px" };
const MAX_IMAGES = 40;

function formatDateTimeStamp(date) {
  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = pad(date.getMinutes());
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const offsetMinutes = -date.getTimezoneOffset();
  const offsetSign = offsetMinutes >= 0 ? "+" : "-";
  const offsetH = pad(Math.floor(Math.abs(offsetMinutes) / 60));
  const offsetM = pad(Math.abs(offsetMinutes) % 60);
  return `${day}/${month}/${year} ${pad(hours)}:${minutes} ${ampm} GMT ${offsetSign}${offsetH}:${offsetM}`;
}

function formatLatLng(lat, lng) {
  const latDir = lat >= 0 ? "N" : "S";
  const lngDir = lng >= 0 ? "E" : "W";
  return `Lat ${Math.abs(lat).toFixed(6)} deg ${latDir}   Long ${Math.abs(lng).toFixed(6)} deg ${lngDir}`;
}

// Converts a JS Date to the "yyyy-MM-ddTHH:mm" shape <input type="datetime-local"> needs.
function toDateTimeLocalValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Wraps text onto multiple canvas lines constrained to maxWidth.
function wrapText(ctx, text, maxWidth) {
  const words = String(text || "").split(" ");
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });
  if (current) lines.push(current);
  return lines;
}

// Draws a simple, reliable vector pin - no external image, so it can never
// taint the canvas or fail to load.
function drawPin(ctx, cx, cy, size, color) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.beginPath();
  ctx.arc(0, 0, size, Math.PI * 0.2, Math.PI * 0.8, true);
  ctx.lineTo(0, size * 2.1);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.42, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();
}

// Draws the GPS-Map-Camera-style overlay directly onto the image canvas: a
// translucent dark panel across the bottom with a pin icon, the short and
// full address, lat/long, and the timestamp.
function drawLocationStamp(canvas, { shortAddress, fullAddress, lat, lng, dateTimeStr }) {
  const ctx = canvas.getContext("2d");
  const w = canvas.width;
  const h = canvas.height;
  const scale = Math.max(1, w / 1000);

  const padding = 22 * scale;
  const pinSize = 12 * scale;
  const textX = padding + pinSize * 3;
  const maxTextWidth = w - textX - padding;

  const shortFontSize = Math.round(20 * scale);
  const smallFontSize = Math.round(14 * scale);
  const shortLineH = shortFontSize * 1.3;
  const smallLineH = smallFontSize * 1.5;

  ctx.font = `${smallFontSize}px Arial, sans-serif`;
  const addressLines = wrapText(ctx, fullAddress, maxTextWidth);
  const latLngLine = formatLatLng(lat, lng);

  const bodyLineCount = addressLines.length + 2;
  const panelHeight = padding * 1.7 + shortLineH + bodyLineCount * smallLineH;
  const panelY = Math.max(0, h - panelHeight);

  const gradient = ctx.createLinearGradient(0, panelY, 0, h);
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(0.4, "rgba(0,0,0,0.6)");
  gradient.addColorStop(1, "rgba(0,0,0,0.78)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, panelY, w, panelHeight);

  let cursorY = panelY + padding * 1.1 + shortLineH * 0.6;
  drawPin(ctx, padding + pinSize, cursorY - shortLineH * 0.3, pinSize, "#ff4d4f");

  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${shortFontSize}px Arial, sans-serif`;
  ctx.fillText(shortAddress, textX, cursorY);
  cursorY += shortLineH * 0.55 + smallLineH * 0.5;

  ctx.font = `${smallFontSize}px Arial, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  addressLines.forEach((line) => {
    ctx.fillText(line, textX, cursorY);
    cursorY += smallLineH;
  });

  ctx.fillText(latLngLine, textX, cursorY);
  cursorY += smallLineH;
  ctx.fillText(dateTimeStr, textX, cursorY);

  ctx.textAlign = "right";
  ctx.font = `${Math.round(12 * scale)}px Arial, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.fillText("Via PRObroker", w - padding, h - padding * 0.4);
  ctx.textAlign = "left";
}

function toDataUrl(canvas, quality = 0.92) {
  return canvas.toDataURL("image/jpeg", quality);
}

// Converts a decimal degree value into the (deg, min, sec) rational triples
// piexifjs expects for GPSLatitude/GPSLongitude.
function degToDmsRational(deg) {
  const absDeg = Math.abs(deg);
  const d = Math.floor(absDeg);
  const minFloat = (absDeg - d) * 60;
  const m = Math.floor(minFloat);
  const s = Math.round((minFloat - m) * 60 * 100);
  return [[d, 1], [m, 1], [s, 100]];
}

// Embeds real GPS coordinates and a timestamp into the JPEG's EXIF data, so
// the file is machine-readable geotagged, not just visibly stamped.
function embedGpsExif(jpegDataUrl, lat, lng, date, address) {
  const exifObj = { "0th": {}, Exif: {}, GPS: {} };
  exifObj.GPS[piexif.GPSIFD.GPSLatitudeRef] = lat >= 0 ? "N" : "S";
  exifObj.GPS[piexif.GPSIFD.GPSLatitude] = degToDmsRational(lat);
  exifObj.GPS[piexif.GPSIFD.GPSLongitudeRef] = lng >= 0 ? "E" : "W";
  exifObj.GPS[piexif.GPSIFD.GPSLongitude] = degToDmsRational(lng);
  const dateStamp = `${date.getFullYear()}:${String(date.getMonth() + 1).padStart(2, "0")}:${String(date.getDate()).padStart(2, "0")}`;
  const dateTimeStamp = `${dateStamp} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
  exifObj.GPS[piexif.GPSIFD.GPSDateStamp] = dateStamp;
  exifObj.GPS[piexif.GPSIFD.GPSTimeStamp] = [[date.getHours(), 1], [date.getMinutes(), 1], [date.getSeconds(), 1]];
  exifObj["0th"][piexif.ImageIFD.DateTime] = dateTimeStamp;
  exifObj.Exif[piexif.ExifIFD.DateTimeOriginal] = dateTimeStamp;
  exifObj.Exif[piexif.ExifIFD.DateTimeDigitized] = dateTimeStamp;
  const safeAddress = (address || "").replace(/[^\x20-\x7E]/g, "").slice(0, 200);
  exifObj["0th"][piexif.ImageIFD.ImageDescription] = safeAddress
    ? `${safeAddress} - Verified via PRObroker`
    : "Verified via PRObroker";
  const exifBytes = piexif.dump(exifObj);
  return piexif.insert(exifBytes, jpegDataUrl);
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

let nextId = 1;

const GeotagPhotos = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "missing-key",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [images, setImages] = useState([]);
  const [location, setLocation] = useState(null);
  const [dateTime, setDateTime] = useState(() => new Date());
  const [processing, setProcessing] = useState(false);
  const [hideWatermark, setHideWatermark] = useState(true);
  const [results, setResults] = useState([]);
  const [geoBusy, setGeoBusy] = useState(false);
  const [manualLat, setManualLat] = useState("");
  const [manualLng, setManualLng] = useState("");
  const [dragOver, setDragOver] = useState(false);

  // Verify Listing paywall (isolated from Plan/Addon system).
  const userId = Cookies.get("userId");
  const [access, setAccess] = useState(null); // null while loading
  const [buyingPlan, setBuyingPlan] = useState(null); // "6M" | "12M" | null while checkout in progress

  const refreshAccess = useCallback(async () => {
    if (!userId) return;
    const res = await apiClient("get", `${VERIFY_LISTING_API}/access/${userId}`);
    if (res.success) setAccess(res.data?.data || null);
  }, [userId]);

  useEffect(() => {
    refreshAccess();
  }, [refreshAccess]);

  const buyVerifyListing = async (planCode) => {
    if (!userId) return;
    setBuyingPlan(planCode);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        alert("Payment SDK failed to load. Please check your connection.");
        return;
      }

      const orderRes = await apiClient("post", `${VERIFY_LISTING_API}/payment/order`, { userId, planCode });
      if (!orderRes.success || !orderRes.data?.order?.id) {
        alert(orderRes.error?.message || "Failed to create payment order");
        return;
      }

      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!razorpayKey) {
        alert("Payment is not configured yet.");
        return;
      }

      const options = {
        key: razorpayKey,
        amount: Math.round(PLAN_PRICING[planCode].amount * 100),
        currency: "INR",
        name: "PRObroker",
        description: `Verify Listing - ${PLAN_PRICING[planCode].label}`,
        order_id: orderRes.data.order.id,
        handler: async (response) => {
          const verifyRes = await apiClient("post", `${VERIFY_LISTING_API}/payment/verify`, {
            userId,
            planCode,
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          });
          if (verifyRes.success) {
            setAccess(verifyRes.data?.data || null);
          } else {
            alert(verifyRes.error?.message || "Payment succeeded but activation failed. Contact support.");
          }
        },
        theme: { color: "#7c3aed" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } finally {
      setBuyingPlan(null);
    }
  };

  const fileInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);

  const missingKey = !GOOGLE_MAPS_API_KEY;

  const getGeocoder = useCallback(() => {
    if (!window.google) return null;
    if (!geocoderRef.current) geocoderRef.current = new window.google.maps.Geocoder();
    return geocoderRef.current;
  }, []);

  const applyLatLng = useCallback(
    (lat, lng) => {
      setResults([]);
      const geocoder = getGeocoder();
      if (!geocoder) {
        setLocation({ lat, lng, shortAddress: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, fullAddress: "" });
        return;
      }
      geocoder.geocode({ location: { lat, lng } }, (resultsList, status) => {
        if (status === "OK" && resultsList && resultsList[0]) {
          const full = resultsList[0].formatted_address;
          const short = full.split(",").slice(0, 2).join(",").trim();
          setLocation({ lat, lng, shortAddress: short, fullAddress: full });
        } else {
          setLocation({ lat, lng, shortAddress: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, fullAddress: "" });
        }
      });
    },
    [getGeocoder]
  );

  const handleFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const mapped = files.map((file) => ({
      id: nextId++,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...mapped].slice(0, MAX_IMAGES));
    setResults([]);
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const removeImage = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
    setResults([]);
  };

  const clearAll = () => {
    setImages([]);
    setResults([]);
  };

  const onMapClick = (e) => {
    applyLatLng(e.latLng.lat(), e.latLng.lng());
  };

  const onAutocompleteLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry?.location) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const full = place.formatted_address || place.name || "";
    const short =
      place.name && full && !full.startsWith(place.name)
        ? place.name
        : full.split(",").slice(0, 2).join(",").trim();
    setResults([]);
    setLocation({ lat, lng, shortAddress: short, fullAddress: full });
    mapRef.current?.panTo({ lat, lng });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Location access is not supported in this browser.");
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        applyLatLng(pos.coords.latitude, pos.coords.longitude);
        mapRef.current?.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoBusy(false);
      },
      () => {
        setGeoBusy(false);
        alert("Could not get your current location. Please allow location access or pick a spot on the map instead.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const applyManualLatLng = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    applyLatLng(lat, lng);
    mapRef.current?.panTo({ lat, lng });
  };

  const generate = async () => {
    if (!images.length || !location) return;
    if (!access?.hasAccess) return;

    // Paid users spend 1 credit per photo -- don't silently process more
    // photos than they have credits for. Trial users are unaffected (1 free
    // run = 1 use, regardless of photo count).
    let queue = images;
    if (access.status === "active") {
      const affordable = Math.max(0, Number(access.credits) || 0);
      if (affordable < images.length) {
        queue = images.slice(0, affordable);
        if (affordable === 0) {
          alert("You're out of credits. Buy more credits to keep verifying photos.");
          return;
        }
        alert(`You have ${affordable} credit(s) left, so only the first ${affordable} photo(s) will be processed. Buy more credits for the rest.`);
      }
    }

    setProcessing(true);
    const dateTimeStr = formatDateTimeStamp(dateTime);
    const output = [];
    for (const item of queue) {
      try {
        const img = await loadImageFromFile(item.file);
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        if (!hideWatermark) {
      drawLocationStamp(canvas, {
          shortAddress: location.shortAddress,
          fullAddress: location.fullAddress || location.shortAddress,
          lat: location.lat,
          lng: location.lng,
          dateTimeStr,
        });
      }
        const stampedDataUrl = toDataUrl(canvas, 0.92);
        const geotaggedDataUrl = embedGpsExif(stampedDataUrl, location.lat, location.lng, dateTime, location.fullAddress || location.shortAddress);
const placeName = (location.shortAddress || "Property").split(",")[0].trim().replace(/[\\/:*?"<>|]+/g, "") || "Property";
        const fileLabel = `PRObroker - ${placeName} Verified Photo ${output.length + 1}`;
        output.push({ id: item.id, name: `${fileLabel}.jpg`, dataUrl: geotaggedDataUrl });
      } catch (err) {
        console.error("Geotag: failed to process", item.file && item.file.name, err);
      }
    }
    setResults(output);
    setProcessing(false);

    if (output.length > 0 && userId) {
      const consumeRes = await apiClient("post", `${VERIFY_LISTING_API}/consume`, {
        userId,
        count: output.length,
      });
      if (consumeRes.success) setAccess(consumeRes.data?.data || null);
    }
  };

  const downloadOne = (result) => {
    const a = document.createElement("a");
    a.href = result.dataUrl;
    a.download = result.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadAll = async () => {
    if (!results.length) return;
    const zip = new JSZip();
    results.forEach((result) => {
      zip.file(result.name, result.dataUrl.split(",")[1], { base64: true });
    });
    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "PRObroker-Verified-Photos.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Rendered in BOTH the normal and paywalled states: a run that spends the
  // last free use flips hasAccess to false, and the freshly stamped photos
  // must stay downloadable instead of vanishing behind the buy screen.
  const resultsPanel = results.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              {results.length} photo(s) geotagged
            </h2>
            <Button type="button" onClick={downloadAll}>
              <Download className="w-4 h-4 mr-2" /> Download All (ZIP)
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {results.map((result) => (
              <div key={result.id} className="relative group">
                <img
                  src={result.dataUrl}
                  alt={result.name}
                  className="w-full aspect-square object-cover rounded-lg border border-gray-200"
                />
                <button
                  onClick={() => downloadOne(result)}
                  className="absolute bottom-2 right-2 bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
  ) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MapPin className="w-6 h-6 text-primary" />
          Verify Listing
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload Property Photos and Get Your Listing Verified Instantly.
        </p>
      </div>

      {access?.status === "trial" && access?.hasAccess && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-3">
          {access.freeUsesRemaining} free verification{access.freeUsesRemaining === 1 ? "" : "s"} remaining on your trial.
        </div>
      )}

      {access?.status === "active" && access?.hasAccess && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-xl px-4 py-3">
          {access.credits} credit{access.credits === 1 ? "" : "s"} remaining (1 credit = 1 photo).
        </div>
      )}

      {access && !access.hasAccess ? (
        <>
        {resultsPanel}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 text-center">
          <Lock className="w-10 h-10 mx-auto text-primary mb-3" />
          <h2 className="text-lg font-bold text-gray-800">
            {access.status === "expired" ? "Your Verify Listing plan has expired" : "You've used all your free verifications"}
          </h2>
          <p className="text-sm text-gray-500 mt-1 mb-6">
            Buy Verify Listing to keep verifying your property photos.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(PLAN_PRICING).map(([code, plan]) => (
              <button
                key={code}
                onClick={() => buyVerifyListing(code)}
                disabled={buyingPlan === code}
                className="border-2 border-primary rounded-xl p-4 hover:bg-primary/5 transition-colors disabled:opacity-60"
              >
                <div className="text-sm font-semibold text-gray-700">{plan.label}</div>
                <div className="text-2xl font-bold text-primary mt-1">₹{plan.amount}</div>
                <div className="text-xs text-gray-400 mt-1">{buyingPlan === code ? "Processing..." : "Buy now"}</div>
              </button>
            ))}
          </div>
        </div>
        </>
      ) : (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-800">1. Upload Photos</h2>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-gray-300 hover:border-primary/50"
            }`}
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">Drag and drop photos here, or click to browse</p>
            <p className="text-xs text-gray-400 mt-1">Up to {MAX_IMAGES} images</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {images.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{images.length} photo(s) selected</span>
                <button onClick={clearAll} className="text-xs text-red-500 hover:underline">
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {images.map((img) => (
                  <div key={img.id} className="relative group aspect-square">
                    <img
                      src={img.previewUrl}
                      alt={img.file.name}
                      className="w-full h-full object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      onClick={() => removeImage(img.id)}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-800">2. Select Location</h2>

          {missingKey ? (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-4">
              Google Maps is not configured yet. Add <code className="font-mono">REACT_APP_GOOGLE_MAPS_API_KEY</code>{" "}
              to the frontend .env (with the Maps JavaScript, Places, and Geocoding APIs enabled and billing on),
              then restart the app. You can still enter latitude/longitude manually below.
            </div>
          ) : loadError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-4">
              Google Maps failed to load. Check that the API key is valid and the required APIs are enabled.
            </div>
          ) : !isLoaded ? (
            <div className="flex items-center justify-center h-64 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged} className="flex-1">
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search for an address or place"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </Autocomplete>
                <Button type="button" variant="outline" onClick={useCurrentLocation} disabled={geoBusy}>
                  {geoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                </Button>
              </div>

              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={location ? { lat: location.lat, lng: location.lng } : DEFAULT_CENTER}
                zoom={location ? 16 : 12}
                onClick={onMapClick}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
              >
                {location && <Marker position={{ lat: location.lat, lng: location.lng }} />}
              </GoogleMap>
              <p className="text-xs text-gray-400">Tap the map to drop a pin at the exact spot.</p>
            </>
          )}



          {location && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
              <div className="font-medium text-gray-800">{location.shortAddress}</div>
              {location.fullAddress && <div className="text-gray-500 text-xs">{location.fullAddress}</div>}
              <div className="text-gray-500 text-xs">{formatLatLng(location.lat, location.lng)}</div>
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 block mb-1">Date and time to stamp on the photos</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(dateTime)}
              onChange={(e) => {
                if (e.target.value) setDateTime(new Date(e.target.value));
                setResults([]);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-full"
            />
          </div>

        {/* <p className="text-xs text-gray-500 mt-1">Or enter coordinates manually:</p> */}


      {/* <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          step="any"
          placeholder="Latitude"
          value={manualLat}
          onChange={(e) => setManualLat(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
        <input
          type="number"
          step="any"
          placeholder="Longitude"
          value={manualLng}
          onChange={(e) => setManualLng(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div> */}
      {/* <Button type="button" variant="outline" size="sm" onClick={applyManualLatLng}>
        Use these coordinates
      </Button> */}

        </div>
      </div>

      <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <input
          type="checkbox"
          id="hideWatermarkToggle"
          checked={hideWatermark}
          onChange={(e) => setHideWatermark(e.target.checked)}
          className="mt-1"
        />
        <label htmlFor="hideWatermarkToggle" className="text-sm text-
gray-700 font-medium">Hide location text on the photo</label>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <Button 
          type="button"
          onClick={generate}
          disabled={!images.length || !location || processing}
          className="w-full sm:w-auto"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Stamping photos...
            </>
          ) : (
            <>
              <MapPin className="w-4 h-4 mr-2" /> Add Location to All Photos
            </>
          )}
        </Button>
        {!images.length && <span className="text-xs text-gray-400">Upload photos first</span>}
        {images.length > 0 && !location && <span className="text-xs text-gray-400">Pick a location first</span>}
      </div>

      {resultsPanel}

      {!images.length && !results.length && (
        <div className="text-center text-gray-400 text-sm py-6 flex flex-col items-center gap-2">
          <ImageIcon className="w-8 h-8" />
          Upload photos and pick a location to get started.
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default GeotagPhotos;
